import React, { useState, useEffect } from 'react';
import { generateClient } from 'aws-amplify/data';
import type { Schema } from '../amplify/data/resource';

import './KeynotesOverview.css';

interface Keynote {
  id: string;
  title: string;
  description: string;
  speaker: string;
  duration: string;
  topic: string;
  targetAudience: string;
  
  // MIGRATION: Keep old field for backward compatibility
  location?: string;
  
  // NEW: Separate address fields
  street?: string;
  houseNumber?: string;
  zip?: string;
  city?: string;
  
  date: string;
  time: string;
  price: string;
  maxAttendees: number;
  currentAttendees: number;
  imageUrl?: string;
  speakerBio?: string;
  objectives?: string;
  prerequisites?: string;
  url?: string;
  isActive: boolean;

  createdAt: string;
  updatedAt: string;
}

const KeynotesOverview: React.FC = () => {
  const [keynotes, setKeynotes] = useState<Keynote[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchKeynotes();
  }, []);

  const fetchKeynotes = async () => {
    try {
      setLoading(true);
      
      // Create client with proper TypeScript typing
      const client = generateClient<Schema>({
        authMode: 'iam'
      });
      
      // Fetch all keynotes - Amplify handles environment isolation automatically
      const { data } = await client.models.Keynote.list();
      console.log('🎤 Keynotes loaded:', data?.length || 0);
      
      // Filter active keynotes only and ensure all required properties exist
      const activeKeynotes = (data || []).filter((keynote: any) => {
        const isValid = keynote && 
               keynote.id && 
               keynote.title && 
               keynote.isActive === true;
        
        if (!isValid) {
          console.log('🎤 Filtering out keynote:', keynote?.title, 'isActive:', keynote?.isActive);
        }
        return isValid;
      });
      
      // Sort keynotes by date (upcoming first)
      const sortedKeynotes = activeKeynotes.sort((a: any, b: any) => {
        if (!a.date && !b.date) return 0;
        if (!a.date) return 1;
        if (!b.date) return -1;
        return new Date(a.date).getTime() - new Date(b.date).getTime();
      });
      
      console.log('🎤 Active keynotes loaded:', sortedKeynotes.length);
      console.log('🎤 Active keynotes details:', sortedKeynotes.map((k: any) => ({
        id: k.id, 
        title: k.title, 
        speaker: k.speaker,
        isActive: k.isActive,
        date: k.date,
        time: k.time
      })));
      
      setKeynotes(sortedKeynotes);
      setError(null);
    } catch (err) {
      console.error('🔴 Error fetching keynotes:', err);
      setError(`Keynotes konnten nicht geladen werden: ${err instanceof Error ? err.message : 'Unbekannter Fehler'}`);
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (price: string) => {
    // Handle different price formats
    if (price.includes('€')) return price;
    if (price === '0' || price === 'kostenlos' || price === 'free') return 'Kostenlos';
    return `€${price}`;
  };

  const formatDate = (date: string) => {
    if (!date) return 'Nach Vereinbarung';
    
    try {
      const dateObj = new Date(date);
      return dateObj.toLocaleDateString('de-DE', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    } catch {
      return date;
    }
  };

  const formatDateShort = (date: string) => {
    if (!date) return 'TBD';
    
    try {
      const dateObj = new Date(date);
      return dateObj.toLocaleDateString('de-DE', {
        day: '2-digit',
        month: '2-digit',
        year: '2-digit'
      });
    } catch {
      return date;
    }
  };

  const formatTime = (time: string) => {
    if (!time) return '';
    
    try {
      // Handle time format (HH:MM or HH:MM:SS)
      const [hours, minutes] = time.split(':');
      return `${hours}:${minutes}`;
    } catch {
      return time;
    }
  };

  const formatAddress = (keynote: Keynote) => {
    // MIGRATION: Use new address fields if available, fallback to old location field
    if (keynote.street || keynote.city) {
      const addressParts = [];
      
      // Street and house number
      if (keynote.street && keynote.houseNumber) {
        addressParts.push(`${keynote.street} ${keynote.houseNumber}`);
      } else if (keynote.street) {
        addressParts.push(keynote.street);
      }
      
      // Zip and city
      if (keynote.zip && keynote.city) {
        addressParts.push(`${keynote.zip} ${keynote.city}`);
      } else if (keynote.city) {
        addressParts.push(keynote.city);
      }
      
      return addressParts.length > 0 ? addressParts.join(', ') : 'Ort wird noch bekannt gegeben';
    }
    
    // Fallback to old location field
    return keynote.location || 'Ort wird noch bekannt gegeben';
  };



  if (loading) {
    return (
      <section className="keynotes-overview">
        <div className="container">
          <div className="keynotes-overview-header">
            <h2>Unsere kommenden <span className="highlight-text">Keynotes</span></h2>
            <p>Erleben Sie inspirierende Vorträge von führenden Experten der KI-Branche.</p>
          </div>
          <div className="keynotes-loading">
            <div className="spinner"></div>
            <p>Keynotes werden geladen...</p>
          </div>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="keynotes-overview">
        <div className="container">
          <div className="keynotes-overview-header">
            <h2>Unsere kommenden <span className="highlight-text">Keynotes</span></h2>
            <p>Erleben Sie inspirierende Vorträge von führenden Experten der KI-Branche.</p>
          </div>
          <div className="keynotes-error">
            <h3>Keynotes können nicht angezeigt werden</h3>
            <p>{error}</p>
            <button onClick={fetchKeynotes} className="retry-btn">
              Erneut versuchen
            </button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="keynotes-overview" data-section="keynotes-overview">
      <div className="container">
        <div className="keynotes-overview-header">
          <h2>Unsere kommenden <span className="highlight-text">Keynotes</span></h2>
          <p>Erleben Sie inspirierende Vorträge von führenden Experten der KI-Branche und erweitern Sie Ihr Wissen in zukunftsweisenden Technologien.</p>
        </div>

        {keynotes.length === 0 ? (
          <div className="keynotes-empty">
            <h3>Derzeit keine Keynotes geplant</h3>
            <p>Schauen Sie bald wieder vorbei oder kontaktieren Sie uns für weitere Informationen über kommende Veranstaltungen.</p>
            <a href="/kontakt" className="contact-btn">
              Kontakt aufnehmen
            </a>
          </div>
        ) : (
          <>
            
            <div className="keynotes-grid">
              {keynotes.map((keynote) => {
                
                return (
                  <div key={keynote.id} className="keynote-card">
                    <div className="keynote-card-header">
                      <div className="keynote-tags">
                        <div className="keynote-topic">{formatDateShort(keynote.date)}</div>
                        {keynote.city && (
                          <div className="keynote-city">{keynote.city}</div>
                        )}
                      </div>
                      <h3 className="keynote-title">{keynote.title}</h3>
                      <div className="keynote-speaker">
                        <span className="speaker-name">{keynote.speaker}</span>
                      </div>
                    </div>
                    
                    <p className="keynote-description">{keynote.description}</p>
                    
                    <div className="keynote-details">
                      <div className="details-columns">
                        <div className="details-column-left">
                          <div className="detail-item">
                            <div className="detail-content">
                              <span className="detail-label">Datum & Zeit</span>
                              <span className="detail-value">
                                {formatDate(keynote.date)}
                                {keynote.time && ` um ${formatTime(keynote.time)}`}
                              </span>
                            </div>
                          </div>
                          
                          <div className="detail-item">
                            <div className="detail-content">
                              <span className="detail-label">Dauer</span>
                              <span className="detail-value">{keynote.duration}</span>
                            </div>
                          </div>
                          
                          <div className="detail-item">
                            <div className="detail-content">
                              <span className="detail-label">Ort</span>
                              <span className="detail-value">{formatAddress(keynote)}</span>
                            </div>
                          </div>
                        </div>
                        
                        <div className="details-column-right">
                          <div className="detail-item">
                            <div className="detail-content">
                              <span className="detail-label">Zielgruppe</span>
                              <span className="detail-value">{keynote.targetAudience}</span>
                            </div>
                          </div>
                          
                          <div className="detail-item">
                            <div className="detail-content">
                              <span className="detail-label">Preis</span>
                              <span className="detail-value price">{formatPrice(keynote.price)}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <div className="keynote-actions">
                      <a 
                        href={keynote.url || "/kontakt"} 
                        className="btn btn-primary keynote-btn"
                        target={keynote.url ? "_blank" : "_self"}
                        rel={keynote.url ? "noopener noreferrer" : undefined}
                      >
                        Informationen und Anmeldung
                      </a>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    </section>
  );
};

export default KeynotesOverview;
